import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Eye, Volume2 } from "lucide-react";
import { playBlend, playSay, playSayHighlight, playReadingAudio, stopReading } from "@/lib/readingAudio";
import { Pic } from "@/components/reading/ReadingExercise";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function Word({ text, silent }) {
  const sil = String(silent || "").split(/[,\s]+/).filter(Boolean);
  if (!sil.length) return text;
  const last = text.replace(/[.!?]$/, "");
  const end = sil.find((s) => last.endsWith(s));
  if (!end) return text;
  return <>{last.slice(0, -end.length)}<span className="text-slate-300" data-testid="flash-silent">{end}</span>{text.slice(last.length)}</>;
}

// Step "Read": child reads the card aloud, then Kiko checks (sounds, then the word) and the picture appears.
export default function FlashCards({ cards, lang, petit, onDone }) {
  const [i, setI] = useState(0);
  const [checked, setChecked] = useState(false);
  const [hi, setHi] = useState(-1);
  const c = cards[i];
  const fr = lang === "fr";
  const parts = String(c?.decoding || c?.text || "").split(c?.kind === "Phrase" ? " / " : " - ").map((s) => s.trim());
  // a silent letter is shown in grey but never said
  const silent = String(c?.silent || "").split(/[,\s]+/).filter(Boolean);
  const spoken = parts.length > 1 && silent.includes(parts[parts.length - 1]) ? parts.slice(0, -1) : parts;

  const check = async () => {
    setChecked(true);
    if (c.kind === "Son") {
      setHi(0); await playReadingAudio(`/${c.text}/`, lang); await wait(300); await playReadingAudio(`/${c.text}/`, lang);
    } else if (c.kind === "Phrase") {
      await playSayHighlight(c.text, lang, setHi);
      return;
    } else if (spoken.length > 1 && (await playBlend(spoken.join(" - "), lang, setHi))) {
      await wait(250);
    } else if (parts.length > 1) {
      for (let k = 0; k < spoken.length; k += 1) {
        setHi(k);
        if (spoken[k].length > 2) await playSay(spoken[k], lang);
        else await playReadingAudio(spoken[k].length === 1 || /^(qu|ck|é|è|ê)$/.test(spoken[k]) ? `/${spoken[k]}/` : spoken[k], lang);
        await wait(120);
      }
    }
    setHi(-1);
    if (c.kind !== "Son") await playSay(c.text, lang);
  };
  const next = () => {
    stopReading();
    setChecked(false);
    if (i + 1 >= cards.length) onDone();
    else setI(i + 1);
  };
  if (!c) return null;
  return (
    <div className="w-full flex flex-col items-center gap-6" data-testid="flash-cards">
      <p className="font-bold text-lg text-slate-600" data-testid="flash-count">
        {fr ? "Lis la carte à voix haute" : "Read the card out loud"} · {i + 1}/{cards.length}
      </p>
      <AnimatePresence mode="wait">
        <motion.div key={c.id} initial={{ rotateY: 90, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }} exit={{ rotateY: -90, opacity: 0 }}
          transition={{ duration: 0.35 }} data-testid="flash-card"
          className="w-full max-w-xl min-h-[260px] rounded-[2rem] bg-white border-[3px] border-slate-900 shadow-[8px_8px_0_0_#0F172A] flex flex-col items-center justify-center gap-4 p-6">
          <div className={`font-bold text-center ${c.kind === "Phrase" ? "text-4xl sm:text-5xl" : petit ? "text-7xl sm:text-8xl" : "text-6xl sm:text-7xl"}`} data-testid="flash-text">
            {checked && parts.length > 1 ? (
              <span className="inline-flex flex-wrap justify-center gap-x-3">
                {parts.map((p, k) => (
                  <motion.span key={k} animate={{ scale: hi === k ? 1.2 : 1, color: hi === k ? "#2563EB" : "#0F172A" }} transition={{ duration: 0.1 }}
                    className={`rounded-lg px-1 ${hi === k ? "bg-yellow-200" : ""}`}>
                    {k === parts.length - 1 ? <Word text={p} silent={c.silent} /> : p}
                  </motion.span>
                ))}
              </span>
            ) : <Word text={c.text} silent={c.silent} />}
          </div>
          {checked && c.picture && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }} className="w-32 h-32">
              <Pic k={c.picture} emoji={c.emoji} label="" className="w-full h-full" />
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
      <div className="flex gap-3 flex-wrap justify-center">
        {!checked ? (
          <motion.button whileTap={{ scale: 0.95 }} onClick={check} data-testid="flash-check"
            className="rounded-full bg-green-500 text-white border-[3px] border-slate-900 px-7 h-14 text-xl font-bold shadow-[4px_4px_0_0_#0F172A] inline-flex items-center gap-2">
            <Eye /> {fr ? "Vérifier avec Kiko" : "Check with Kiko"}
          </motion.button>
        ) : (
          <>
            <button onClick={check} data-testid="flash-replay"
              className="rounded-full bg-yellow-300 border-[3px] border-slate-900 px-5 h-14 font-bold shadow-[4px_4px_0_0_#0F172A] inline-flex items-center gap-2">
              <Volume2 /> {fr ? "Réécouter" : "Listen again"}
            </button>
            <motion.button initial={{ scale: 0.8 }} animate={{ scale: 1 }} onClick={next} data-testid="flash-next"
              className="rounded-full bg-blue-600 text-white border-[3px] border-slate-900 px-7 h-14 text-xl font-bold shadow-[4px_4px_0_0_#0F172A] inline-flex items-center gap-2">
              {fr ? "Carte suivante" : "Next card"} <ArrowRight />
            </motion.button>
          </>
        )}
      </div>
    </div>
  );
}
